import { USER_ROLES } from "@nlc/shared-types";

interface RequestCounts {
  active: number;
  all: number;
  archived: number;
  completed: number;
  drafts: number;
  paused: number;
  queued: number;
}

interface SuperAdminRequestCounts {
  completed: number;
  dueToday: number;
  open: number;
  overdue: number;
  paused: number;
  queued: number;
  readyForReview: number;
}

// Combined counts that includes all possible properties
export interface CombinedCounts
  extends RequestCounts,
    SuperAdminRequestCounts {}

/**
 * Utility functions for role-based logic in the requests feature
 */

/**
 * Check if user has customer role (owner, admin, collaborator)
 * @param userRole - The user's role string (can be null)
 * @returns True if user has customer role, false otherwise
 */
export function isCustomerUser(userRole?: null | string): boolean {
  return (
    userRole === USER_ROLES.OWNER ||
    userRole === USER_ROLES.ADMIN ||
    userRole === USER_ROLES.COLLABORATOR
  );
}

/**
 * Check if user is SuperAdmin
 * @param userRole - The user's role string (can be null)
 * @returns True if user is SuperAdmin, false otherwise
 */
export function isSuperAdminUser(userRole?: null | string): boolean {
  return userRole === USER_ROLES.SUPER_ADMIN;
}

/**
 * Check if user has team role (super admin, team leader, designer)
 * @param userRole - The user's role string (can be null)
 * @returns True if user has team role, false otherwise
 */
export function isTeamUser(userRole?: null | string): boolean {
  return (
    userRole === USER_ROLES.SUPER_ADMIN ||
    userRole === USER_ROLES.TEAM_LEADER ||
    userRole === USER_ROLES.DESIGNER
  );
}

/**
 * Check if user is Team Leader
 * @param userRole - The user's role string (can be null)
 * @returns True if user is Team Leader, false otherwise
 */
export function isTeamLeaderUser(userRole?: null | string): boolean {
  return userRole === USER_ROLES.TEAM_LEADER;
}

/**
 * Check if user is Designer
 * @param userRole - The user's role string (can be null)
 * @returns True if user is Designer, false otherwise
 */
export function isDesignerUser(userRole?: null | string): boolean {
  return userRole === USER_ROLES.DESIGNER;
}

/**
 * Get the appropriate count data based on user role
 * @param userRole - The user's role string (can be null)
 * @param customerCounts - Counts data for customer users
 * @param superAdminCounts - Counts data for SuperAdmin users
 * @returns The appropriate counts object
 */
export function getCountsByRole(
  userRole?: null | string,
  customerCounts?: RequestCounts,
  superAdminCounts?: SuperAdminRequestCounts,
): CombinedCounts {
  if (isSuperAdminUser(userRole) && superAdminCounts) {
    return {
      // Customer counts (required properties)
      active: customerCounts?.active || 0,
      all: customerCounts?.all || 0,
      archived: customerCounts?.archived || 0,
      completed: superAdminCounts.completed || 0, // Use superAdmin completed count
      drafts: customerCounts?.drafts || 0,
      // SuperAdmin specific counts
      dueToday: superAdminCounts.dueToday || 0,
      open: superAdminCounts.open || 0,
      overdue: superAdminCounts.overdue || 0,
      paused: superAdminCounts.paused || 0,
      queued: superAdminCounts.queued || customerCounts?.queued || 0,
      readyForReview: superAdminCounts.readyForReview || 0,
    };
  }

  // Return customer counts with defaults for missing properties
  return {
    active: customerCounts?.active || 0,
    all: customerCounts?.all || 0,
    archived: customerCounts?.archived || 0,
    completed: customerCounts?.completed || 0,
    drafts: customerCounts?.drafts || 0,
    // Default superAdmin counts to 0 for customer users
    dueToday: 0,
    open: 0,
    overdue: 0,
    paused: customerCounts?.paused || 0,
    queued: customerCounts?.queued || 0,
    readyForReview: 0,
  };
}

/**
 * Get the default tab based on user role
 * @param userRole - The user's role string (can be null)
 * @returns The default tab name
 */
export function getDefaultTabByRole(userRole?: null | string): string {
  if (isSuperAdminUser(userRole)) {
    return "open"; // SuperAdmin starts with open tab
  }

  if (isTeamLeaderUser(userRole)) {
    return "assigned-to-me"; // Team Leaders start with assigned-to-me tab
  }

  if (isDesignerUser(userRole)) {
    return "assigned-to-me"; // Designers start with assigned-to-me tab
  }

  return "active"; // Customers start with active tab
}

/**
 * Check if a feature should be visible based on user role
 * @param userRole - The user's role string (can be null)
 * @param feature - The feature to check ('statistics' | 'filters' | 'createButton' | 'teamLeadDropdown' | 'designerDropdown')
 * @returns True if feature should be visible, false otherwise
 */
export function shouldShowFeature(
  userRole: null | string | undefined,
  feature:
    | "createButton"
    | "designerDropdown"
    | "filters"
    | "statistics"
    | "teamLeadDropdown",
): boolean {
  if (feature === "statistics" || feature === "filters") {
    return isCustomerUser(userRole);
  }

  if (feature === "teamLeadDropdown") {
    return isTeamLeaderUser(userRole);
  }

  if (feature === "designerDropdown") {
    return isDesignerUser(userRole);
  }

  if (feature === "createButton") {
    // Hide for SuperAdmin, Team Leader, and Designer
    return (
      !isSuperAdminUser(userRole) &&
      !isTeamLeaderUser(userRole) &&
      !isDesignerUser(userRole)
    );
  }

  // This should never happen with proper TypeScript usage
  return true;
}
